import { rolePermissions } from "../_permissions/rolePermissions";
import { docPermConstants } from "../_constants/permissionConstants";

/**
 * Generic access control provider for links, based on claim and
 * target action/docType.
 * @param {Object} authClaim - User claim info and login status
 * @param {Object} authTarget - Target action and docType
 */
export function authorizeLinkAccess(authClaim, authTarget) {
  const { user, loggedIn } = authClaim.authentication;
  const { action, docType } = authTarget;
  if (loggedIn) {
    if (
      user.userRole.userRoleClass === "TE" &&
      rolePermissions.NONPROJECT_STAFF[docType].includes(action)
    )
      return true;
    else if (
      (user.userRole.userRoleClass === "SENTEBE" ||
        user.userRole.userRoleClass === "SUPTEBE") &&
      rolePermissions.NONPROJECT_SUPERVISOR[docType].includes(action)
    )
      return true;
    else return false;
  }
}

/**
 * Returns an array of permissions for the requested docType, based on the
 * user's project role.
 * @param {Object} authRequest - authorization request. See 1st line for shape.
 * @returns {string[]}
 */
export function getPermissions(authRequest) {
  // Shape of authRequest. 'user' is the redux user object.
  const { user, docType, projectId, creatorId } = authRequest;
  // Is user verified? docType provided?
  if (user && docType) {
    // DISTADMINs have special privileges, so first check all non-DISTADMIN cases.
    // There are two non-DISTADMIN auth cases: those that require projectId,
    // and those that don't. Examples of the first case include ADR,
    // ProjAssignment, and ProjAdmin. Examples of the second case
    // include ReportSelectionModal and AdrSearchModal.

    // Test the first case:
    if (!user.isOfficeAdmin && projectId) {
      // Get user's project role for the provided project
      var userPr =
        user.projectRoles && user.projectRoles.find((prc) => prc.projectId === projectId);

      // If there's error in fetching the role permisions, return least privileges.
      if (
        !userPr ||
        !(userPr.projectRoleClass in rolePermissions) ||
        !(docType in rolePermissions[userPr.projectRoleClass])
      )
        return rolePermissions.UNVERIFIED[docType];

      // Fetch permissions
      let perm = rolePermissions[userPr.projectRoleClass][docType];
      const authorOnly = [
        docPermConstants.AUTHOR_VIEW,
        docPermConstants.AUTHOR_EDIT,
        docPermConstants.AUTHOR_DELETE,
      ];

      // If any author-only permissions are found in the fetched permissions...
      if (perm.some((p) => authorOnly.includes(p))) {
        // If requesting user is not the author, or creatorId is not provided in
        // authRequest, strip away the AUTHOR_XXX permissions.
        // Else, do nothing and keep AUTHOR_XXX in the fetched permissions.
        if (!creatorId || user.userId !== creatorId)
          perm = perm.filter((p) => !authorOnly.includes(p));
      }

      // Return permissions
      return perm;
    }
    // Test the second case:
    else if (!user.isOfficeAdmin && !projectId) {
      switch (user.userRole.userRoleClass) {
        case "SENTEBE":
        case "SUPTEBE":
          return rolePermissions.NONPROJECT_SUPERVISOR[docType];
        case "TE":
          return rolePermissions.NONPROJECT_STAFF[docType];
        default:
          return rolePermissions.UNVERIFIED[docType];
      }
    } else if (user.isOfficeAdmin && !user.isSystemAdmin) {
      // Return permissions for distAdmin who's not a SysAdmin
      return rolePermissions.DISTADMIN[docType];
    } else if (user.isSystemAdmin) {
      // Return permissions for SysAdmin
      return rolePermissions.SYSADMIN[docType];
    }
  }

  // User is not verified, or incorrect/unverifiable params provided
  return rolePermissions.UNVERIFIED[docType];
}
